import { useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Bot, Loader2, Send } from "lucide-react";
import { useAssumirConversa, useCriarCampanha } from "@/hooks/useCrm";
import { useCrmDashboard } from "@/hooks/useCrmDashboard";
import { ESTAGIO_COLOR, ESTAGIO_LABEL, type CrmEstagio } from "@/types/crm";
import { formatDataHora, iniciais, type CrmView } from "./crmUtils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type Props = {
  onNavigate: (view: CrmView, opts?: { conversaId?: string; minhas?: boolean; estagio?: CrmEstagio }) => void;
  onNovaCampanha: () => void;
};

function KpiDelta({ percent, recentes }: { percent: number | null; recentes: number }) {
  if (percent === null) {
    return <span className="text-xs text-fynix-muted">{recentes} nos últimos 7 dias</span>;
  }
  const up = percent >= 0;
  return (
    <span className={`text-xs font-medium ${up ? "text-fynix-brand-dark" : "text-red-500"}`}>
      {up ? "+" : ""}
      {percent}% · 7 dias
    </span>
  );
}

export function CrmHome({ onNavigate, onNovaCampanha }: Props) {
  const {
    isLoading,
    naoLidas,
    abertas,
    novos,
    humano,
    ganho,
    conversao,
    kpiNovos,
    kpiHumano,
    kpiGanho,
    semanas,
    donut,
    donutTotal,
    recentes,
    proximaFila,
  } = useCrmDashboard();
  const assumir = useAssumirConversa();

  const semanasTemDados = semanas.some((s) => s.inbound + s.campanha > 0);

  const handleAssumirProxima = () => {
    if (!proximaFila) {
      onNavigate("inbox");
      return;
    }
    assumir.mutate(proximaFila.id, {
      onSuccess: () => onNavigate("inbox", { conversaId: proximaFila.id }),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-4 relative overflow-hidden rounded-3xl bg-gradient-to-br from-fynix-mist to-fynix-brand p-6 text-white min-h-[220px]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full bg-white/15" />
          <div className="pointer-events-none absolute -bottom-12 right-8 h-36 w-36 rounded-full bg-white/10" />
          <p className="text-xs uppercase tracking-widest text-white/80">Inbox</p>
          <p className="crm-title mt-3 text-4xl">{naoLidas}</p>
          <p className="mt-1 text-sm text-white/85">mensagens não lidas</p>
          <p className="mt-2 text-sm text-white/70">{abertas} conversas abertas no funil</p>
          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={() => onNavigate("inbox")}
              className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-fynix-brand hover:bg-fynix-brand-soft"
            >
              Ir ao Inbox
            </button>
            <button
              type="button"
              onClick={handleAssumirProxima}
              disabled={assumir.isPending}
              className="rounded-2xl bg-fynix-brand-dark px-4 py-2 text-sm font-semibold text-white hover:bg-black/80 disabled:opacity-60"
            >
              Assumir atendimento
            </button>
          </div>
        </div>

        <div className="crm-card xl:col-span-5 p-5">
          <p className="text-xs uppercase tracking-widest text-fynix-muted">Funil agora</p>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-fynix-muted">Novos</p>
              <p className="crm-title mt-1 text-2xl text-fynix-ink">{novos}</p>
              <KpiDelta {...kpiNovos} />
            </div>
            <div className="border-x border-fynix-line px-4">
              <p className="text-xs text-fynix-muted">Humano atendendo</p>
              <p className="crm-title mt-1 text-2xl text-fynix-ink">{humano}</p>
              <KpiDelta {...kpiHumano} />
            </div>
            <div>
              <p className="text-xs text-fynix-muted">Fechados ganho</p>
              <p className="crm-title mt-1 text-2xl text-fynix-ink">{ganho}</p>
              <KpiDelta {...kpiGanho} />
            </div>
          </div>
        </div>

        <div className="crm-card xl:col-span-3 p-5">
          <p className="text-xs uppercase tracking-widest text-fynix-muted">Conversão</p>
          <p className="mt-2 text-sm font-medium text-fynix-brand-dark">
            {conversao >= 60 ? "Excelente" : conversao >= 30 ? "Em ritmo" : "Acompanhar"}
          </p>
          <p className="crm-title mt-1 text-4xl text-fynix-ink">{conversao}%</p>
          <div className="mt-4 h-2.5 rounded-full bg-fynix-surface-panel">
            <div
              className="h-2.5 rounded-full bg-gradient-to-r from-fynix-brand to-fynix-brand-mid"
              style={{ width: `${Math.min(conversao, 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-fynix-muted">Ganho sobre encerradas (ganho + perdido + sem interesse)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="crm-card xl:col-span-8 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="crm-title text-lg">Volume semanal</h3>
              <p className="text-xs text-fynix-muted">Entradas orgânicas vs. campanhas</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-fynix-muted">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-fynix-brand-dark" /> Inbound
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-fynix-brand" /> Campanha
              </span>
            </div>
          </div>
          {semanasTemDados ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={semanas} barGap={6}>
                  <XAxis dataKey="semana" tick={{ fill: "#6B7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "#E8E9EA", opacity: 0.6 }}
                    contentStyle={{ borderRadius: 16, borderColor: "#E5E7EB" }}
                  />
                  <Bar dataKey="inbound" name="Inbound" fill="#1A2022" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="campanha" name="Campanha" fill="#4B5563" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-fynix-muted">
              Ainda não há conversas para montar o gráfico. Elas aparecem com o WhatsApp ou campanhas.
            </div>
          )}
        </div>

        <div className="crm-card xl:col-span-4 p-5 flex flex-col">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-fynix-brand-soft text-fynix-brand-dark flex items-center justify-center">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h3 className="crm-title text-lg">Atalhos</h3>
              <p className="text-xs text-fynix-muted">O que você precisa agora?</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="crm-chip px-3 py-1.5 text-sm" onClick={() => onNavigate("inbox", { minhas: true })}>
              Minhas conversas
            </button>
            <button
              type="button"
              className="crm-chip px-3 py-1.5 text-sm"
              onClick={() => onNavigate("kanban", { estagio: "ia_atendendo" })}
            >
              Fila IA
            </button>
            <button type="button" className="crm-chip px-3 py-1.5 text-sm" onClick={onNovaCampanha}>
              Nova campanha
            </button>
            <button
              type="button"
              className="crm-chip px-3 py-1.5 text-sm"
              onClick={() => onNavigate("kanban", { estagio: "renovacao_andamento" })}
            >
              Renovação
            </button>
          </div>
          <div className="mt-auto pt-6 text-xs text-fynix-muted">
            {proximaFila
              ? `Próxima da fila: ${proximaFila.nome_contato || proximaFila.telefone_cliente}`
              : "Fila de novos/aguardando vazia."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="crm-card xl:col-span-6 p-5">
          <h3 className="crm-title text-lg mb-3">Conversas recentes</h3>
          {recentes.length === 0 ? (
            <p className="text-sm text-fynix-muted py-10 text-center">
              Nenhuma conversa ainda. Quando um cliente falar no WhatsApp, ela entra aqui.
            </p>
          ) : (
            <div className="divide-y divide-fynix-line">
              {recentes.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onNavigate("inbox", { conversaId: c.id })}
                  className="w-full flex items-center gap-3 py-3 text-left hover:bg-fynix-surface-muted rounded-2xl px-1"
                >
                  <div className="h-9 w-9 rounded-full bg-fynix-brand-soft text-fynix-brand-dark flex items-center justify-center text-xs font-semibold shrink-0">
                    {iniciais(c.nome_contato, c.telefone_cliente)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{c.nome_contato || c.telefone_cliente}</span>
                      <span className="text-[11px] text-fynix-muted shrink-0">{formatDataHora(c.ultima_msg_em)}</span>
                    </div>
                    <p className="text-xs text-fynix-muted truncate">{c.ultima_msg_preview || "—"}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ESTAGIO_COLOR[c.estagio]}`}>
                    {ESTAGIO_LABEL[c.estagio]}
                  </span>
                  {c.nao_lidas > 0 && (
                    <span className="h-5 min-w-5 px-1 rounded-full bg-fynix-brand text-white text-[10px] flex items-center justify-center">
                      {c.nao_lidas}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="crm-card xl:col-span-3 p-5">
          <h3 className="crm-title text-lg mb-3">Estágios</h3>
          {donutTotal === 0 ? (
            <p className="text-sm text-fynix-muted py-10 text-center">Sem dados de funil.</p>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut} dataKey="value" nameKey="label" innerRadius={48} outerRadius={70} paddingAngle={3}>
                      {donut.map((g) => (
                        <Cell key={g.key} fill={g.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 16, borderColor: "#E5E7EB" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-2 space-y-1.5">
                {donut.map((g) => (
                  <li key={g.key} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-fynix-ink-soft">
                      <span className="h-2 w-2 rounded-full" style={{ background: g.color }} />
                      {g.label}
                    </span>
                    <span className="font-semibold">{Math.round((g.value / donutTotal) * 100)}%</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <CampanhaRapida onGoCampanhas={() => onNavigate("campanhas")} />
      </div>
    </div>
  );
}

function CampanhaRapida({ onGoCampanhas }: { onGoCampanhas: () => void }) {
  const criar = useCriarCampanha();
  const [nome, setNome] = useState("");
  const [mensagem, setMensagem] = useState("");
  const pode = nome.trim() && mensagem.trim();

  return (
    <div className="crm-card xl:col-span-3 p-5 flex flex-col">
      <h3 className="crm-title text-lg">Campanha rápida</h3>
      <p className="text-xs text-fynix-muted mb-3">Salva como rascunho para disparar depois.</p>
      <div className="space-y-3 flex-1">
        <div className="space-y-1">
          <Label className="text-xs">Nome</Label>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Renovação abril" className="rounded-2xl" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Mensagem</Label>
          <Textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            rows={4}
            placeholder="Olá {nome}, …"
            className="rounded-2xl resize-none"
          />
        </div>
      </div>
      <button
        type="button"
        disabled={!pode || criar.isPending}
        onClick={() => {
          criar.mutate(
            { nome: nome.trim(), tipo: "marketing", mensagem_texto: mensagem.trim() },
            {
              onSuccess: () => {
                setNome("");
                setMensagem("");
                onGoCampanhas();
              },
            }
          );
        }}
        className="crm-btn-primary mt-4 w-full py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {criar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Salvar rascunho
      </button>
    </div>
  );
}
